<script setup lang="ts">
/**
 * CustomTag Component for Portal Package
 * 
 * A simplified tag component that directly uses our predefined offerte status colors.
 * Adapted from the frontend CustomTag component for use in the portal package.
 * 
 * @example
 * <CustomTag color="nieuw" label="Nieuw" />
 * <CustomTag color="verzonden" icon="pi pi-circle-fill" />
 * <CustomTag color="alles_geaccepteerd" label="Geaccepteerd" icon="pi pi-check" />
 */

import { computed } from 'vue';

interface Props {
  /** Color key that maps to CSS variable (e.g., "nieuw" → --color-nieuw) */
  color: string;
  /** Text content to display in the tag */
  label?: string;
  /** PrimeIcon class name (e.g., "pi pi-circle-fill") */
  icon?: string;
  /** Make the tag clickable */
  clickable?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

/**
 * Computed CSS custom property name based on the color prop
 * Maps "nieuw" → "var(--color-nieuw)", "aangepast" → "var(--color-aangepast)", etc.
 */
const cssColorVar = computed(() => `var(--color-${props.color})`);

/**
 * Computed styles object for the tag element
 */
const tagStyles = computed(() => ({
  backgroundColor: cssColorVar.value,
  color: '#ffffff'
}));

/**
 * Computed classes for the tag element
 */
const tagClasses = computed(() => [
  'custom-tag',
  {
    'custom-tag--clickable': props.clickable
  }
]);

/**
 * Handle click events
 */
const handleClick = (event: MouseEvent) => {
  if (props.clickable) {
    emit('click', event);
  }
};
</script>

<template>
  <span 
    :class="tagClasses"
    :style="tagStyles"
    @click="handleClick"
  >
    <i v-if="icon" :class="icon" class="custom-tag__icon"></i>
    <span v-if="label" class="custom-tag__label">{{ label }}</span>
  </span>
</template>

<style scoped>
.custom-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  gap: 0.25rem;
  line-height: 1;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.custom-tag--clickable {
  cursor: pointer;
}

.custom-tag--clickable:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.custom-tag__icon {
  font-size: 0.75rem;
}

.custom-tag__label {
  font-weight: 600;
}

/* Ensure proper spacing when both icon and label are present */
.custom-tag__icon + .custom-tag__label {
  margin-left: 0.125rem;
}
</style>
